/**
 * zip.js
 * A minimal ZIP writer, so a folder of resized pictures comes back as one file.
 *
 * Written out rather than pulled in as a dependency for two reasons. The
 * bigger one: everything else on the Resize screen happens in the browser with
 * nothing uploaded and nothing installed, and adding a library to bundle the
 * results would be the only part of it that needed the internet. The smaller
 * one: this is about a hundred lines, and the alternative is tens of kilobytes
 * of general-purpose compression for files that are already compressed.
 *
 * Stored, not deflated. A JPEG or a PNG is compressed already — running it
 * through deflate saves a percent or two and costs seconds per file. "Store"
 * is the honest choice here, and every unzip tool has supported it since 1989.
 *
 * The format, for anyone maintaining this:
 *
 *   [local header + data] per file
 *   [central directory entry] per file
 *   [end of central directory]
 *
 * Each central entry points back at its local header by byte offset, which is
 * why the offsets are tracked as it goes rather than computed afterwards.
 */

/**
 * CRC-32, as ZIP requires it.
 *
 * The table is built once on first use — 256 entries, and computing it lazily
 * keeps the cost off the initial page load for a screen most visits never open.
 */
let TABLE = null;

function crcTable() {
  if (TABLE) return TABLE;

  TABLE = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let value = i;
    for (let bit = 0; bit < 8; bit++) {
      // 0xEDB88320 is the reversed polynomial ZIP and PNG both use.
      value = value & 1 ? (value >>> 1) ^ 0xedb88320 : value >>> 1;
    }
    TABLE[i] = value >>> 0;
  }
  return TABLE;
}

export function crc32(bytes) {
  const table = crcTable();
  let crc = 0xffffffff;

  for (let i = 0; i < bytes.length; i++) {
    crc = (crc >>> 8) ^ table[(crc ^ bytes[i]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * A date and time in the MS-DOS format ZIP still uses.
 *
 * Two-second resolution and an epoch of 1980 — which is why anything earlier
 * is clamped rather than allowed to wrap into a nonsense year.
 */
export function dosDateTime(date = new Date()) {
  const year = Math.max(1980, date.getFullYear());

  const time =
    (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const day = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();

  return { time: time & 0xffff, date: day & 0xffff };
}

/** Names inside a ZIP are UTF-8 bytes, and must not escape the archive. */
export function safeEntryName(name) {
  return String(name ?? 'file')
    .replace(/\\/g, '/')
    // A leading slash or any ".." segment would let an archive write outside
    // the folder it is extracted into. Not a risk we create, but not one to
    // pass along either.
    .split('/')
    .filter((part) => part && part !== '.' && part !== '..')
    .join('/')
    .slice(0, 200) || 'file';
}

/**
 * Builds the archive.
 *
 * @param {Array<{name: string, bytes: Uint8Array}>} entries
 * @param {{onProgress?: (done: number, total: number) => void}} [options]
 * @returns {Promise<Blob>}
 */
export async function createZip(entries, { onProgress } = {}) {
  const encoder = new TextEncoder();
  const chunks = [];
  const central = [];

  let offset = 0;
  let index = 0;

  for (const entry of entries) {
    const name = encoder.encode(safeEntryName(entry.name));
    const bytes = entry.bytes;
    const crc = crc32(bytes);
    const { time, date } = dosDateTime(entry.date);

    // ---- local file header ----
    const header = new DataView(new ArrayBuffer(30));
    header.setUint32(0, 0x04034b50, true); // signature
    header.setUint16(4, 20, true); // version needed
    header.setUint16(6, 0x0800, true); // flags: name is UTF-8
    header.setUint16(8, 0, true); // method: stored
    header.setUint16(10, time, true);
    header.setUint16(12, date, true);
    header.setUint32(14, crc, true);
    header.setUint32(18, bytes.length, true); // compressed
    header.setUint32(22, bytes.length, true); // uncompressed
    header.setUint16(26, name.length, true);
    header.setUint16(28, 0, true); // extra

    chunks.push(new Uint8Array(header.buffer), name, bytes);

    // ---- remembered for the central directory ----
    central.push({ name, crc, size: bytes.length, time, date, offset });
    offset += 30 + name.length + bytes.length;

    onProgress?.(++index, entries.length);
    // Yield between files so a hundred of them does not freeze the tab.
    if (index % 5 === 0) await new Promise((r) => setTimeout(r, 0));
  }

  // ---- central directory ----
  const directoryStart = offset;

  for (const item of central) {
    const record = new DataView(new ArrayBuffer(46));
    record.setUint32(0, 0x02014b50, true);
    record.setUint16(4, 20, true); // version made by
    record.setUint16(6, 20, true); // version needed
    record.setUint16(8, 0x0800, true);
    record.setUint16(10, 0, true); // stored
    record.setUint16(12, item.time, true);
    record.setUint16(14, item.date, true);
    record.setUint32(16, item.crc, true);
    record.setUint32(20, item.size, true);
    record.setUint32(24, item.size, true);
    record.setUint16(28, item.name.length, true);
    record.setUint16(30, 0, true); // extra
    record.setUint16(32, 0, true); // comment
    record.setUint16(34, 0, true); // disk number
    record.setUint16(36, 0, true); // internal attributes
    record.setUint32(38, 0, true); // external attributes
    record.setUint32(42, item.offset, true);

    chunks.push(new Uint8Array(record.buffer), item.name);
    offset += 46 + item.name.length;
  }

  // ---- end of central directory ----
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(4, 0, true); // this disk
  end.setUint16(6, 0, true); // disk with the directory
  end.setUint16(8, central.length, true);
  end.setUint16(10, central.length, true);
  end.setUint32(12, offset - directoryStart, true);
  end.setUint32(16, directoryStart, true);
  end.setUint16(20, 0, true); // comment length

  chunks.push(new Uint8Array(end.buffer));

  return new Blob(chunks, { type: 'application/zip' });
}

/**
 * Every image file inside something dropped on the page, folders included.
 *
 * A dropped folder arrives as a `FileSystemEntry` rather than a `File`, and
 * has to be walked. Depth is bounded because a symlinked loop would otherwise
 * hang the tab, and the count is bounded because someone will eventually drop
 * their Pictures folder.
 *
 * @param {DataTransferItemList} items
 */
export async function filesFromDrop(items, { maxFiles = 200, maxDepth = 6 } = {}) {
  const entries = [];

  // Captured before any await: the list is emptied once the drop event ends.
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
    else {
      const file = item.getAsFile?.();
      if (file) entries.push(file);
    }
  }

  const found = [];

  async function walk(entry, depth) {
    if (found.length >= maxFiles || depth > maxDepth) return;

    // A plain File, from a browser with no entry API.
    if (entry instanceof File) {
      if (entry.type.startsWith('image/')) found.push(entry);
      return;
    }

    if (entry.isFile) {
      const file = await new Promise((resolve) => entry.file(resolve, () => resolve(null)));
      if (file?.type.startsWith('image/')) found.push(file);
      return;
    }

    if (entry.isDirectory) {
      const reader = entry.createReader();

      // readEntries returns at most a hundred at a time and must be called
      // until it returns none — a single call silently truncates a big folder.
      for (;;) {
        const batch = await new Promise((resolve) =>
          reader.readEntries(resolve, () => resolve([])),
        );
        if (batch.length === 0) break;
        for (const child of batch) await walk(child, depth + 1);
        if (found.length >= maxFiles) break;
      }
    }
  }

  for (const entry of entries) await walk(entry, 0);
  return found;
}
